"use client";
import {
  fetchAndSetUserSubscriptionStatus,
  setMandateStatusAction,
} from "@/actions/user.actions";
import { apiSdkInstance } from "@/apiSdk/stageApiSdk.instance";
import {
  MandateTransactionStatusEnum,
  PAYMENT_POLLING_COUNT,
  PAYMENT_POLLING_GAP_DELAY,
  SUBSCRIPTION_STATUS,
  WindowEventListenersEnum,
} from "@/constants/constants";
import { useUserContext } from "@/context/UserProvider";
import { useTrackingAnalytics } from "@/hooks/useTrackingAnalytics";
import { InitPaymentPollingEventParams } from "@/types/payment.types";
import {
  generateCryptoBasedUUID,
  paymentFailedRedirection,
  paymentSuccessRedirection,
} from "@/utils/common.helpers";
import { captureError } from "@/utils/common.helpers";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";

export const usePaymentPolling = () => {
  const router = useRouter();
  const { trackEvent } = useTrackingAnalytics();
  const { updateSubscription, user } = useUserContext();

  const [isRedirected, setIsRedirected] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attemptsRef = useRef<number>(0);
  const shouldPollRef = useRef<boolean>(true);

  const clearPollingInterval = useCallback(() => {
    // Set flag to stop polling
    if (timeoutRef.current) {
      sendToSupabase({
        poolNumber: 0,
        userId: user.userId,
        eventName: "polling_stopped",
        eventValue: "polling_stopped",
      });
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, [user]);

  useEffect(() => {
    const updateSubscriptionContextApi = (
      event: CustomEvent<InitPaymentPollingEventParams>,
    ) => {
      const { isNewPaymentFlow, showPaymentFailPage } = event.detail;
      const urlSearchParams = new URLSearchParams(window.location.search);
      const redirect = urlSearchParams.get("redirectionAfterPaymentTo");

      initPaymentPolling(
        updateSubscription,
        redirect,
        isNewPaymentFlow,
        showPaymentFailPage,
      );
    };
    const stopPaymentPolling = () => {
      shouldPollRef.current = false;
      clearPollingInterval();
    };
    window.addEventListener(
      WindowEventListenersEnum.INITPAYMENTPOLLING,
      updateSubscriptionContextApi as EventListener,
    );

    window.addEventListener(
      WindowEventListenersEnum.STOPPAYMENTPOLLING,
      stopPaymentPolling,
    );
    return () => {
      window.removeEventListener(
        WindowEventListenersEnum.INITPAYMENTPOLLING,
        updateSubscriptionContextApi as EventListener,
      );
      window.removeEventListener(
        WindowEventListenersEnum.STOPPAYMENTPOLLING,
        stopPaymentPolling,
      );
    };
  }, [user, updateSubscription, clearPollingInterval]);

  const initPaymentPolling = (
    updateSubscriptionContextApi: (abc: number) => void,
    redirectTo: string | null,
    isNewPaymentFlow: boolean,
    showPaymentFailPage: boolean,
  ) => {
    sendToSupabase({
      poolNumber: 0,
      userId: user.userId,
      eventName: "polling_started",
      eventValue: "polling_started",
    });

    // Dispatch event to notify polling has started
    window.dispatchEvent(
      new CustomEvent(WindowEventListenersEnum.PAYMENT_POLLING_STARTED),
    );

    const handlePaymentSuccess = async () => {
      clearPollingInterval();
      updateSubscriptionContextApi(SUBSCRIPTION_STATUS.SUBSCRIBED);

      // Update mandate status cookies (subscriptionStatus, trialDaysElapsed)
      // This ensures retention events can be tracked after subscription
      await setMandateStatusAction();

      // Dispatch event to notify payment success
      window.dispatchEvent(
        new CustomEvent(WindowEventListenersEnum.PAYMENT_STATUS_SUCCESS),
      );

      await paymentSuccessRedirection(
        isRedirected,
        null,
        router,
        trackEvent,
        redirectTo,
      );
    };

    const handlePaymentFailed = async () => {
      clearPollingInterval();

      // Dispatch event to notify payment failed
      window.dispatchEvent(
        new CustomEvent(WindowEventListenersEnum.PAYMENT_STATUS_FAILED),
      );

      await paymentFailedRedirection(
        isRedirected,
        trackEvent,
        {},
        null,
        router,
        redirectTo,
        showPaymentFailPage,
      );
    };

    function updatePaymentStatus() {
      attemptsRef.current = 0;
      shouldPollRef.current = true; // Reset flag when starting new polling
      clearPollingInterval(); // Clear any existing timeout before starting a new one

      const poll = async () => {
        // Check if polling should continue
        if (!shouldPollRef.current) {
          return;
        }
        if (attemptsRef.current >= PAYMENT_POLLING_COUNT) {
          clearPollingInterval();
          return;
        }

        try {
          if (isNewPaymentFlow) {
            sendToSupabase({
              poolNumber: attemptsRef.current,
              userId: user.userId,
              eventName: "api_call_started",
              eventValue: "api_call_started",
            });

            const transactionStatus =
              await apiSdkInstance.payment.getTransactionStatus();

            sendToSupabase({
              poolNumber: attemptsRef.current,
              userId: user.userId,
              eventName: "api_call_ended",
              eventValue: transactionStatus,
            });

            if (transactionStatus === MandateTransactionStatusEnum.SUCCESS) {
              await handlePaymentSuccess();
              return;
            }
            if (transactionStatus === MandateTransactionStatusEnum.FAILED) {
              await handlePaymentFailed();
              return;
            }
          } else {
            const { subscriptionStatus } =
              await fetchAndSetUserSubscriptionStatus(false, true, false);

            if (subscriptionStatus === SUBSCRIPTION_STATUS.SUBSCRIBED) {
              await handlePaymentSuccess();
              return;
            }
          }
          // Check flag before continuing
          if (shouldPollRef.current) {
            continuePolling();
          }
        } catch (error) {
          const isTimeoutError =
            error instanceof Error &&
            (error.name === "AbortError" || // for safari
              error.name === "TimeoutError" || // for chrome and firefox
              error.message === "signal timed out");

          if (isTimeoutError) {
            // if the api call is timed out, continue polling
            continuePolling();
            return;
          }
          console.error("update pooling error", error);
          clearPollingInterval();
          captureError(error);
        }
      };

      const continuePolling = () => {
        // Check if polling should continue before scheduling next poll
        if (!shouldPollRef.current) {
          return;
        }
        attemptsRef.current++;
        timeoutRef.current = setTimeout(poll, PAYMENT_POLLING_GAP_DELAY);
      };

      // Start the first poll
      poll();
    }
    updatePaymentStatus();
  };
};

const sendToSupabase = async ({
  poolNumber,
  userId,
  eventName,
  eventValue,
}: {
  poolNumber: number;
  userId: string;
  eventName: string;
  eventValue: string;
}) => {
  // TODO: Remove this return statement to enable debug logging for multiple StartTrial firing issues
  return;
  window.supaId = userId || window.supaId || "c_" + generateCryptoBasedUUID();

  try {
    const response = await fetch("/api/send-to-supabase", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        pool_number: poolNumber,
        user_id: window.supaId,
        event_name: eventName,
        event_value: eventValue,
      }),
    });
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error sending to Supabase:", error);
    throw error;
  }
};
